import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
];

const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;
const fmtHrs = (n) => `${(n ?? 0).toFixed(1)}h`;

export default function Analytics() {
  const [range, setRange] = useState("7d");
  const [overview, setOverview] = useState(null);
  const [stylists, setStylists] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  // Per-button download state — keeps the three export buttons
  // independent so clicking "Services" doesn't grey out "Stylists".
  const [exporting, setExporting] = useState(null); // one of: "overview" | "stylists" | "services" | null
  const [heatmap, setHeatmap] = useState(null);

  /**
   * Download a CSV from one of the export endpoints.
   *
   * We use axios `responseType: 'blob'` so the JWT interceptor still
   * applies (a plain <a href> won't carry the Authorization header).
   * The resulting Blob is converted to an object-URL and clicked
   * programmatically — same pattern Chrome/Sheets use for their own
   * exports. The user stays on the Analytics page throughout.
   *
   * No scheduling logic is invoked by this function — it is a pure
   * HTTP GET that downloads bytes produced by the backend.
   */
  const downloadCsv = async (kind) => {
    setExporting(kind);
    try {
      const res = await api.get(`/analytics/export/${kind}.csv`, {
        params: { range },
        responseType: "blob",
      });
      const filename = `analytics-${kind}-${range}.csv`;
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Release the object URL on the next tick — clicking it kicks
      // off the download, but revoking too early can race in Safari.
      setTimeout(() => window.URL.revokeObjectURL(url), 200);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [o, s, sv, hm] = await Promise.all([
        api.get(`/analytics/overview?range=${range}`).then((r) => r.data),
        api.get(`/analytics/stylists?range=${range}`).then((r) => r.data),
        api.get(`/analytics/services?range=${range}`).then((r) => r.data),
        api.get(`/analytics/heatmap?range=${range}`).then((r) => r.data),
      ]);
      if (cancelled) return;
      setOverview(o);
      setStylists(s.stylists || []);
      setServices(sv.services || []);
      setHeatmap(hm);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [range]);

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Operations</div>
          <h1 className="font-heading text-4xl tracking-tight font-light mt-2">Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Observational metrics derived from existing bookings. Read-only — nothing here changes how the salon schedules.
          </p>
        </div>
        <div
          className="flex border border-border rounded-md overflow-hidden"
          role="radiogroup"
          aria-label="Range"
          data-testid="analytics-range-toggle"
        >
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              role="radio"
              aria-checked={range === r.key}
              data-testid={`analytics-range-${r.key}`}
              className={`px-4 py-2 text-sm transition-colors ${
                range === r.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-white hover:bg-secondary"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* CSV exports — read-only operational reporting. Range is
          inherited from the selector above; no separate state. */}
      <div
        className="flex items-center gap-3 flex-wrap"
        data-testid="analytics-export-bar"
      >
        <span className="text-xs uppercase tracking-wide text-muted-foreground font-mono">
          Export CSV
        </span>
        {[
          { kind: "overview", label: "Overview" },
          { kind: "stylists", label: "Stylists" },
          { kind: "services", label: "Services" },
        ].map((b) => (
          <button
            key={b.kind}
            type="button"
            onClick={() => downloadCsv(b.kind)}
            disabled={exporting === b.kind}
            data-testid={`analytics-export-${b.kind}`}
            aria-label={`Download ${b.label} analytics as CSV for the selected range`}
            className="px-3 py-1.5 text-xs border border-border rounded-md bg-white hover:bg-secondary disabled:opacity-50 disabled:cursor-wait transition-colors"
          >
            {exporting === b.kind ? `Preparing ${b.label}…` : b.label}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpis">
        <KpiCard testid="kpi-total-bookings" label="Total bookings" value={overview?.total_bookings ?? 0} sub={`${overview?.completed_bookings ?? 0} completed · ${overview?.cancelled_bookings ?? 0} cancelled`} />
        <KpiCard testid="kpi-completion-rate" label="Completion rate" value={fmtPct(overview?.completion_rate)} sub={`Cancelled: ${fmtPct(overview?.cancellation_rate)}`} />
        <KpiCard testid="kpi-occupied-hours" label="Occupied hours" value={fmtHrs(overview?.occupied_hours)} sub={`Processing: ${fmtHrs(overview?.processing_hours)}`} />
        <KpiCard testid="kpi-parallel-bookings" label="Parallel bookings" value={overview?.parallel_bookings ?? 0} sub={`${fmtPct(overview?.overlap_percentage)} of bookings`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="analytics-secondary">
        <KpiCard
          testid="kpi-reschedules"
          label="Reschedules"
          value={overview?.rescheduled_bookings ?? 0}
          sub={`${fmtPct(overview?.reschedule_rate)} of bookings`}
        />
        <KpiCard
          testid="kpi-unused-processing-windows"
          label="Unused processing windows"
          value={overview?.unused_processing_windows ?? 0}
          sub={`${fmtHrs(overview?.unused_processing_hours)} unused stylist availability`}
        />
        <KpiCard
          testid="kpi-processing-share"
          label="Processing share"
          value={
            overview && (overview.occupied_hours + overview.processing_hours) > 0
              ? fmtPct(100 * overview.processing_hours / (overview.occupied_hours + overview.processing_hours))
              : "0.0%"
          }
          sub="Customer-in-service time during which the stylist is free"
        />
      </div>

      {/* Per stylist */}
      <section className="surface-card p-6" data-testid="analytics-stylist-table">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-heading text-2xl tracking-tight">Stylist utilisation</h2>
          <span className="text-xs text-muted-foreground">
            {loading ? "Refreshing…" : `${stylists.length} stylists`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-2 pr-4 overline">Stylist</th>
                <th className="py-2 px-2 overline text-right">Bookings</th>
                <th className="py-2 px-2 overline text-right">Occupied</th>
                <th className="py-2 px-2 overline text-right">Processing</th>
                <th className="py-2 px-2 overline text-right">Parallel</th>
                <th className="py-2 px-2 overline text-right">Unused windows</th>
                <th className="py-2 px-2 overline">Utilisation</th>
              </tr>
            </thead>
            <tbody>
              {stylists.map((s) => {
                const occ = s.occupied_hours || 0;
                const proc = s.processing_hours || 0;
                const unused = s.unused_processing_hours || 0;
                const total = occ + proc + unused || 1;
                return (
                  <tr
                    key={s.stylist_id}
                    className="border-b border-border/60 last:border-b-0"
                    data-testid={`stylist-row-${s.stylist_id}`}
                  >
                    <td className="py-2 pr-4">
                      <div className="font-medium">{s.stylist_name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono uppercase tracking-wide">
                        cap {s.max_parallel_bookings} · {s.active ? "active" : "off"}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums">{s.total_bookings}</td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums">{fmtHrs(occ)}</td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums">{fmtHrs(proc)}</td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums">{s.parallel_bookings}</td>
                    <td className="py-2 px-2 text-right font-mono tabular-nums">{s.unused_processing_windows}</td>
                    <td className="py-2 px-2">
                      <div
                        className="flex w-full h-3 rounded-sm overflow-hidden border border-border"
                        data-testid={`stylist-bar-${s.stylist_id}`}
                        title={`Occupied ${fmtHrs(occ)} · Processing ${fmtHrs(proc)} · Unused ${fmtHrs(unused)}`}
                      >
                        <div style={{ width: `${(occ / total) * 100}%`, background: "#1A3626" }} />
                        <div style={{
                          width: `${(proc / total) * 100}%`,
                          background: "repeating-linear-gradient(135deg, #D1E0D7 0 6px, #B8CCBE 6px 12px)",
                        }} />
                        <div style={{ width: `${(unused / total) * 100}%`, background: "#EFEBE3" }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
              {stylists.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                    No stylist activity in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="flex items-center gap-4 mt-3 text-[10px] uppercase tracking-wide text-muted-foreground">
            <Legend color="#1A3626" label="Occupied" />
            <Legend pattern label="Processing" />
            <Legend color="#EFEBE3" label="Unused processing" />
          </div>
        </div>
      </section>

      {/* Salon Activity Heatmap */}
      <ActivityHeatmap data={heatmap} loading={loading} />

      {/* Per service */}
      <section className="surface-card p-6" data-testid="analytics-service-table">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-heading text-2xl tracking-tight">Service breakdown</h2>
          <span className="text-xs text-muted-foreground">{services.length} services with activity</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-2 pr-4 overline">Service</th>
                <th className="py-2 px-2 overline text-right">Bookings</th>
                <th className="py-2 px-2 overline text-right">Cancelled</th>
                <th className="py-2 px-2 overline text-right">Avg dur.</th>
                <th className="py-2 px-2 overline text-right">Occupied</th>
                <th className="py-2 px-2 overline text-right">Processing</th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr
                  key={s.service_id}
                  className="border-b border-border/60 last:border-b-0"
                  data-testid={`service-row-${s.service_id}`}
                >
                  <td className="py-2 pr-4">
                    <div className="font-medium">{s.service_name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                      {s.active ? "active" : "archived"}
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums">{s.total_bookings}</td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums">{s.cancelled_bookings} ({fmtPct(s.cancellation_rate)})</td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums">{s.average_duration_minutes}m</td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums">{fmtHrs(s.occupied_hours)}</td>
                  <td className="py-2 px-2 text-right font-mono tabular-nums">{fmtHrs(s.processing_hours)}</td>
                </tr>
              ))}
              {services.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                    No service activity in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function KpiCard({ label, value, sub, testid }) {
  return (
    <div className="surface-card p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-heading text-3xl tracking-tight mt-2 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function Legend({ color, pattern, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block w-3 h-3 rounded-sm border border-border/50"
        style={
          pattern
            ? { background: "repeating-linear-gradient(135deg, #D1E0D7 0 4px, #B8CCBE 4px 8px)" }
            : { background: color }
        }
      />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Heatmap constants (module-level for stable references)
// ---------------------------------------------------------------------------
const HM_HOURS = Array.from({ length: 13 }, (_, i) => 8 + i); // 08–20
const HM_DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtHour(h) {
  return `${String(h).padStart(2, "0")}:00`;
}

function hmIntensity(slot, overlay) {
  if (!slot || !slot.booking_count) return 0;
  if (overlay === "bookings") return slot.booking_count;
  if (overlay === "processing") return slot.processing_minutes;
  return slot.occupied_minutes + 0.35 * slot.processing_minutes;
}

function hmCellBg(slot, maxVal, overlay) {
  const v = hmIntensity(slot, overlay);
  if (!v || maxVal === 0) return "rgba(26,54,38,0.04)";
  const t = Math.min(1, v / maxVal);
  const opacity = 0.10 + t * 0.90;
  return `rgba(26,54,38,${opacity.toFixed(2)})`;
}

function hmTextColor(slot, maxVal, overlay) {
  if (!slot || maxVal === 0) return "transparent";
  const t = hmIntensity(slot, overlay) / maxVal;
  return t > 0.58 ? "rgba(255,255,255,0.92)" : "rgba(26,54,38,0.55)";
}

// ---------------------------------------------------------------------------
// Heatmap main component
// ---------------------------------------------------------------------------
function ActivityHeatmap({ data, loading }) {
  const [overlay, setOverlay] = useState("occupied");
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);

  if (loading || !data) {
    return (
      <section className="surface-card p-6" data-testid="analytics-heatmap-skeleton">
        <div className="h-6 w-52 bg-muted/60 rounded animate-pulse mb-4" />
        <div className="space-y-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="flex gap-1" style={{ height: 32 }}>
              <div className="rounded bg-muted/40 animate-pulse" style={{ width: 72 }} />
              {Array.from({ length: 13 }).map((_, j) => (
                <div
                  key={j}
                  className="flex-1 rounded bg-muted/30 animate-pulse"
                  style={{ animationDelay: `${(i * 13 + j) * 15}ms` }}
                />
              ))}
            </div>
          ))}
        </div>
      </section>
    );
  }

  const days = data.days ?? [];
  const allSlots = days.flatMap((d) =>
    d.slots.map((s) => ({ ...s, day: d.day, dayIndex: d.day_index }))
  );
  const totalActivity = allSlots.reduce((acc, s) => acc + s.booking_count, 0);
  const maxVal = Math.max(1, ...allSlots.map((s) => hmIntensity(s, overlay)));

  // Peak insights (derived entirely from dataset)
  const peakDayObj = [...days].sort((a, b) => b.total_bookings - a.total_bookings)[0] ?? null;
  const peakSlot = allSlots.length
    ? allSlots.reduce((a, b) => hmIntensity(a, "occupied") >= hmIntensity(b, "occupied") ? a : b)
    : null;
  const parallelSlot = allSlots.length
    ? allSlots.reduce((a, b) => (b.parallel_count > a.parallel_count ? b : a))
    : null;
  const activeSlots = allSlots.filter((s) => s.booking_count > 0);
  const quietSlot = activeSlots.length
    ? activeSlots.reduce((a, b) => hmIntensity(a, "occupied") <= hmIntensity(b, "occupied") ? a : b)
    : null;

  return (
    <section className="surface-card p-6" data-testid="analytics-heatmap">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="font-heading text-2xl tracking-tight">Salon Activity Heatmap</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Booking density across days and hours. Intensity represents occupied working load.
          </p>
        </div>
        {/* Overlay toggle */}
        <div
          className="flex border border-border rounded-md overflow-hidden text-xs"
          role="radiogroup"
          aria-label="Heatmap overlay"
          data-testid="heatmap-overlay-toggle"
        >
          {[
            { key: "occupied", label: "Occupied" },
            { key: "processing", label: "Processing" },
            { key: "bookings", label: "Bookings" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setOverlay(key)}
              role="radio"
              aria-checked={overlay === key}
              data-testid={`heatmap-overlay-${key}`}
              className={`px-3 py-1.5 transition-colors ${
                overlay === key
                  ? "bg-[#1A3626] text-white"
                  : "bg-white hover:bg-secondary"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Peak insights */}
      {totalActivity > 0 && (
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
          data-testid="heatmap-peak-insights"
        >
          <PeakStat
            testid="heatmap-peak-day"
            label="Peak day"
            value={peakDayObj?.day ?? "—"}
            sub={peakDayObj ? `${peakDayObj.total_bookings} bookings` : ""}
          />
          <PeakStat
            testid="heatmap-peak-window"
            label="Peak window"
            value={peakSlot ? `${fmtHour(peakSlot.hour)}–${fmtHour(peakSlot.hour + 1)}` : "—"}
            sub={peakSlot?.day ?? ""}
          />
          <PeakStat
            testid="heatmap-peak-parallel"
            label="Highest overlap"
            value={
              parallelSlot && parallelSlot.parallel_count > 0
                ? `${parallelSlot.parallel_count} parallel`
                : "None"
            }
            sub={
              parallelSlot && parallelSlot.parallel_count > 0
                ? `${parallelSlot.day} ${fmtHour(parallelSlot.hour)}`
                : ""
            }
          />
          <PeakStat
            testid="heatmap-quiet-window"
            label="Quietest window"
            value={quietSlot ? `${fmtHour(quietSlot.hour)}–${fmtHour(quietSlot.hour + 1)}` : "—"}
            sub={quietSlot?.day ?? ""}
          />
        </div>
      )}

      {/* Grid or empty state */}
      {totalActivity === 0 ? (
        <div
          className="py-14 text-center text-sm text-muted-foreground"
          data-testid="heatmap-empty"
        >
          No booking activity in this window.
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="heatmap-grid-container">
          <div style={{ minWidth: 540 }}>
            {/* Hour header row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "72px repeat(13, minmax(28px, 1fr))",
                marginBottom: 3,
              }}
              data-testid="heatmap-hour-header"
            >
              <div />
              {HM_HOURS.map((h) => (
                <div
                  key={h}
                  className="text-center font-mono"
                  style={{ fontSize: 9, color: "#5C635A" }}
                  data-testid={`heatmap-hour-label-${h}`}
                >
                  {fmtHour(h)}
                </div>
              ))}
            </div>

            {/* Data rows */}
            {days.map((d) => {
              const slotByHour = Object.fromEntries(d.slots.map((s) => [s.hour, s]));
              return (
                <div
                  key={d.day}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "72px repeat(13, minmax(28px, 1fr))",
                    marginBottom: 3,
                  }}
                  data-testid={`heatmap-row-${d.day.toLowerCase()}`}
                >
                  {/* Day label */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#5C635A",
                      paddingRight: 8,
                    }}
                  >
                    {HM_DAY_SHORT[d.day_index]}
                  </div>

                  {/* Hour cells */}
                  {HM_HOURS.map((h) => {
                    const slot = slotByHour[h];
                    const hasData = !!slot && slot.booking_count > 0;
                    const cnt = slot?.booking_count ?? 0;
                    return (
                      <div
                        key={h}
                        data-testid={`heatmap-cell-${d.day.toLowerCase()}-${h}`}
                        style={{
                          height: 32,
                          borderRadius: 3,
                          margin: "0 1px",
                          backgroundColor: hmCellBg(slot, maxVal, overlay),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: hasData ? "pointer" : "default",
                          transition: "filter 0.12s",
                        }}
                        onMouseEnter={
                          hasData
                            ? (e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltip({ day: d.day, hour: h, data: slot, rect });
                              }
                            : undefined
                        }
                        onMouseLeave={() => setTooltip(null)}
                        aria-label={
                          hasData
                            ? `${d.day} ${fmtHour(h)}: ${cnt} bookings`
                            : undefined
                        }
                      >
                        {hasData && cnt >= 3 && (
                          <span
                            style={{
                              fontSize: 9,
                              fontFamily: "monospace",
                              color: hmTextColor(slot, maxVal, overlay),
                              pointerEvents: "none",
                            }}
                          >
                            {cnt}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Color scale legend */}
      {totalActivity > 0 && (
        <div className="flex items-center gap-2 mt-4 text-[10px] text-muted-foreground flex-wrap">
          <span>Low</span>
          <div
            style={{
              width: 80,
              height: 10,
              borderRadius: 2,
              background: "linear-gradient(to right, rgba(26,54,38,0.10), rgba(26,54,38,1.0))",
              border: "1px solid rgba(26,54,38,0.12)",
            }}
            data-testid="heatmap-legend-scale"
          />
          <span>High</span>
          <span className="ml-auto opacity-70">
            {overlay === "occupied" && "Occupied load + 35% processing"}
            {overlay === "processing" && "Processing minutes"}
            {overlay === "bookings" && "Booking count"}
          </span>
        </div>
      )}

      {/* Hover tooltip (fixed-position, above the cell) */}
      {tooltip && (
        <HeatmapTooltip tooltip={tooltip} ref={tooltipRef} />
      )}
    </section>
  );
}

function PeakStat({ label, value, sub, testid }) {
  return (
    <div
      className="rounded-md p-3"
      style={{ background: "rgba(26,54,38,0.04)", border: "1px solid rgba(26,54,38,0.08)" }}
      data-testid={testid}
    >
      <div className="overline text-[10px]">{label}</div>
      <div className="font-heading text-base tracking-tight mt-1">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function HeatmapTooltip({ tooltip }) {
  const { day, hour, data, rect } = tooltip;
  // Position above and centred on the cell
  const top = rect.top + window.scrollY - 8;
  const left = rect.left + rect.width / 2;
  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{
        top: `${rect.top - 8}px`,
        left: `${left}px`,
        transform: "translate(-50%, -100%)",
      }}
      data-testid="heatmap-tooltip"
    >
      <div className="surface-card shadow-md p-3 text-xs" style={{ minWidth: 168 }}>
        <div className="font-medium mb-2 text-foreground">
          {day} {fmtHour(hour)}–{fmtHour(hour + 1)}
        </div>
        <div className="space-y-1">
          <TooltipRow label="Bookings" value={data.booking_count} />
          <TooltipRow label="Occupied" value={`${data.occupied_minutes}m`} />
          <TooltipRow label="Processing" value={`${data.processing_minutes}m`} />
          <TooltipRow label="Parallel overlaps" value={data.parallel_count} />
        </div>
      </div>
    </div>
  );
}

function TooltipRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-medium text-foreground">{value}</span>
    </div>
  );
}
